import { colors } from './UI/colors';

import type { SxProps, Theme } from '@mui/material/styles';

export const headerStyles = {
	headerAppBar: {
		backgroundColor: colors.white,
		zIndex: 1100,
		boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.10)',
		'--header-height': '64px',
	} as SxProps<Theme>,

	headerHidden: {
		transform: 'translateY(-100%)',
		transition: 'transform 200ms ease',
	} as SxProps<Theme>,

	headerVisible: {
		transform: 'translateY(0)',
		transition: 'transform 200ms ease',
	} as SxProps<Theme>,

	container: {
		maxWidth: '1440px',
		paddingLeft: '16px',
		paddingRight: '16px',
		'@media (min-width: 600px)': {
			paddingLeft: '24px',
			paddingRight: '24px',
		},
	} as SxProps<Theme>,

	toolbar: {
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingTop: '8px',
		paddingBottom: '8px',
		paddingLeft: '0 !important',
		paddingRight: '0 !important',
		minHeight: '56px',
		'@media (min-width: 600px)': {
			paddingTop: '12px',
			paddingBottom: '12px',
			minHeight: '64px',
		},
	} as SxProps<Theme>,

	menuButton: {
		border: `1px solid ${colors.borderGray}`,
		borderRadius: '50%',
		padding: '12px',
		color: colors.dark,
		'&:hover': {
			backgroundColor: colors.borderGray,
			color: colors.dark,
		},
	} as SxProps<Theme>,

	menuIcon: {
		color: colors.dark,
	} as SxProps<Theme>,

	desktopLogo: {
		display: 'none',
		alignItems: 'center',
		'@media (min-width: 1024px)': {
			display: 'flex',
		},
	} as SxProps<Theme>,

	mobileLogo: {
		display: 'flex',
		alignItems: 'center',
		'@media (min-width: 1024px)': {
			display: 'none',
		},
	} as SxProps<Theme>,

	// =========================
	// HeaderDrawer styles
	// =========================

	drawerPaper: {
		width: '100vw',
		maxWidth: '25rem',
		borderRadius: '0 2rem 0 0',
		backgroundColor: '#f0f0f0',
		overflowX: 'hidden',
	} as SxProps<Theme>,

	drawerRootContainer: {
		backgroundColor: '#f0f0f0',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		borderRadius: '2rem',
		height: '100%',
	} as SxProps<Theme>,

	drawerHeaderContainer: {
		padding: '1.75rem 1.25rem 0.75rem',
		display: 'flex',
		alignItems: 'center',
		gap: '1.25rem',
		boxShadow: '0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.05)',
		position: 'sticky',
		top: 0,
		backgroundColor: colors.white,
		zIndex: 2,
	} as SxProps<Theme>,

	closeButton: {
		borderRadius: '3.125rem',
		border: '0.055rem solid rgba(167, 173, 176, 0.6)',
	} as SxProps<Theme>,

	locationCardActionsRoot: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
		width: '100%',
	} as SxProps<Theme>,

	officeButtonsRowWrapper: {
		position: 'relative',
		width: '100%',
	} as SxProps<Theme>,

	officeButtonsRowWrapperGradient: {
		'&::after': {
			content: '""',
			position: 'absolute',
			top: 0,
			right: 0,
			width: '32px',
			height: '100%',
			background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 100%)',
			pointerEvents: 'none',
			opacity: 1,
			transition: 'opacity 0.2s ease',
			zIndex: 1,
		},
	} as SxProps<Theme>,

	officeButtonsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.25rem',
		flexWrap: 'nowrap',
		overflowX: 'auto',
		whiteSpace: 'nowrap',
		position: 'relative',
		'&::-webkit-scrollbar': {
			display: 'none',
		},
	} as SxProps<Theme>,

	officeDetailsColumn: {
		display: 'flex',
		alignItems: 'flex-start',
		flexDirection: 'column',
		gap: '0.25rem',
	} as SxProps<Theme>,

	officeDetailsOverflow: {
		overflowX: 'hidden',
	} as SxProps<Theme>,

	reviewsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.25rem',
	} as SxProps<Theme>,

	starsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.25rem',
	} as SxProps<Theme>,

	mobileFooter: {
		display: 'none',
		position: 'fixed',
		bottom: 0,
		left: 0,
		width: '100%',
		backgroundColor: colors.white,
		padding: '0.75rem 1.25rem',
		boxShadow: '0 -2px 8px rgba(0,0,0,0.10)',
		zIndex: 1100,
		justifyContent: 'center',
		alignItems: 'center',

		'@media (max-width: 1200px)': {
			display: 'flex',
		},
	} as SxProps<Theme>,
};

export const headerActionsStyles = {
	desktopActions: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		'@media (max-width: 1200px)': {
			display: 'none',
		},
	} as SxProps<Theme>,

	actionGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		padding: '4px',
		borderRadius: '54px',
		border: `1px solid ${colors.borderGray}`,
		backgroundColor: colors.white,
	} as SxProps<Theme>,

	reasonSelectWrapper: {
		minWidth: '140px',
		display: 'none',
		'@media (min-width: 1200px)': { display: 'block' },
	} as SxProps<Theme>,

	verticalDivider: {
		display: 'none',
		'@media (min-width: 1200px)': { display: 'block' },
		height: '32px',
		borderColor: colors.borderGray,
		margin: 0,
	} as SxProps<Theme>,

	officeNameButton: {
		'&:hover': {
			textDecoration: 'none',
		},
	} as SxProps<Theme>,

	phoneButtonDesktop: {
		display: 'none',
		'@media (min-width: 1200px)': { display: 'inline-flex' },
		fontWeight: 600,
	} as SxProps<Theme>,

	userIconWrapper: {
		display: 'flex',
		alignItems: 'center',
	} as SxProps<Theme>,
};

export const locationDrawerStyles = {
	locationSearchContainer: {
		display: 'flex',
		gap: '8px',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '4px 20px 8px',
	} as SxProps<Theme>,

	searchErrorBox: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
		border: '1px solid',
		borderColor: colors.text.secondary,
		borderRadius: '8px',
		padding: '12px 16px',
		margin: '0 20px 8px',
	} as SxProps<Theme>,

	searchErrorIcon: {
		color: colors.text.secondary,
		flexShrink: 0,
		marginTop: '2px',
	} as SxProps<Theme>,

	searchErrorText: {
		color: colors.text.primary,
	} as SxProps<Theme>,

	officeListStack: {
		marginBottom: '1.5rem',
	} as SxProps<Theme>,

	officeCard: {
		borderRadius: '1.25rem',
		padding: '1.25rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		backgroundColor: colors.white,
	} as SxProps<Theme>,

	officeCardSelected: {
		border: '2px solid #027356',
	} as SxProps<Theme>,

	officeCardInner: {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
	} as SxProps<Theme>,

	officeNameRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	} as SxProps<Theme>,

	officeName: {
		fontWeight: 600,
	} as SxProps<Theme>,

	officeDetailsOverflow: {
		overflowX: 'hidden',
	} as SxProps<Theme>,

	radioNoPadding: {
		padding: 0,
	} as SxProps<Theme>,

	locationFooterActions: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1.25rem',
		padding: '1.25rem',
		position: 'fixed',
		bottom: 0,
		width: '100%',
		maxWidth: '23rem',
		zIndex: 2000,
	} as SxProps<Theme>,
};

// ======================================
// MenuDrawer + MenuList styles
// ======================================
export const menuDrawerStyles = {
	selectedOfficeContainer: {
		gap: '0.75rem',
		padding: '0.75rem',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		borderRadius: '0.75rem',
		backgroundColor: colors.white,
	} as SxProps<Theme>,

	changeLocation: {
		textDecoration: 'underline',
		color: colors.text.secondary,
		'&:hover': { textDecoration: 'underline' },
	} as SxProps<Theme>,

	drawerLinksContainer: {
		padding: '0.5rem 1.25rem',
		backgroundColor: colors.white,
		borderRadius: '1.5rem',
	} as SxProps<Theme>,

	drawerLinksGroupHeader: {
		borderRadius: '2rem',
		display: 'flex',
		alignItems: 'center',
		marginBottom: '0.5rem',
		'& .MuiIconButton-root': {
			marginRight: '8px',
			padding: 0,
			color: '#4d4d4d',
		},
	} as SxProps<Theme>,

	menuListRoot: {
		gap: '0.5rem',
		display: 'flex',
		flexDirection: 'column',
	} as SxProps<Theme>,

	menuListItemButton: {
		p: 0,
	} as SxProps<Theme>,

	menuListItemText: {
		height: '3rem',
		m: 0,
		display: 'flex',
		alignItems: 'center',
	} as SxProps<Theme>,

	menuChevron: {
		color: colors.text.secondary,
	} as SxProps<Theme>,
};
